import { useEffect, useRef, useState } from "react";
import { lifestyleImages, lifestyleDescriptions } from "@/mocks/landingData";

const items = [
  {
    key: "stayActive" as const,
    image: lifestyleImages.stayActive,
  },
  {
    key: "familyTime" as const,
    image: lifestyleImages.familyTime,
  },
  {
    key: "independence" as const,
    image: lifestyleImages.independence,
  },
  {
    key: "enjoyTravel" as const,
    image: lifestyleImages.enjoyTravel,
  },
];

export default function LifestyleSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-16 md:py-24 bg-background-50">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-600 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)" }}
        >
          <p className="text-accent-500 text-base font-medium tracking-[0.2em] uppercase mb-3">
            What It Means
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
            The Life on the Other Side
          </h2>
          <p className="text-foreground-700 text-xl max-w-2xl mx-auto">
            Supporting your joints is really about supporting the moments that
            make a life.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((item, idx) => {
            const desc = lifestyleDescriptions[item.key];
            return (
              <div
                key={item.key}
                className={`rounded-xl overflow-hidden border border-background-200/70 card-hover-lift transition-all duration-600 ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
                }`}
                style={{
                  transitionDelay: `${idx * 80}ms`,
                  transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
                }}
              >
                <div className="h-64 md:h-72 overflow-hidden rounded-t-xl">
                  <img
                    src={item.image}
                    alt={desc.title}
                    className="w-full h-full object-cover hover-img-zoom"
                    width={800}
                    height={1000}
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
                    {desc.title}
                  </h3>
                  <p className="text-foreground-700 text-lg leading-relaxed">
                    {desc.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}